import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { Button, Cell, DataBlock, Grid, InputGroup, Row, H5, DangerButton } from '@fs/zion-ui'
import { useParams } from '@fs/zion-router'
import { PersonLink, UserLink } from '../../app/Links'
import { printValueOrBooleanReadable } from '../../../util/StringUtil'
import { convertUTCString, ORIGINAL_DATE_FORMAT } from '../../../util/DateUtil'
import { TreeIdRows } from '../messagesList/messageSummaries/TreeIdBlock'
import { getMessageById } from '../../../services/dmqueuesService'
import { getHolocaustReport } from '../../../services/QueuesService'
import useNotifications from '../../../hooks/useNotifications'

const iFrameCss = css`
  height: 0px;
  width: 0px;
  position: absolute;
`

export function HolocaustReportDetailsView({ backToMessagesList, buttonTemplates }) {
  const { t } = useTranslation()
  const { error } = useNotifications()
  const [report, setReport] = useState(null)
  const [message, setMessage] = useState(null)

  const pathParams = useParams()

  useEffect(() => {
    getMessageById(pathParams.messageId)
      .then((theMessage) => {
        setMessage(theMessage.data)
        return getHolocaustReport(theMessage.data.details.personId, theMessage.data.details.userId)
          .then((data) => {
            setReport(data)
          })
          .catch((err) => {
            error(
              t('queues.errors.loadingReport', {
                defaultValue: 'Error loading holocaust report for message {{messageId}}',
                messageId: pathParams.messageId,
              }),
              err
            )
          })
      })
      .catch((err) => {
        error(
          t('queues.errors.loadingMessage', {
            defaultValue: 'Error loading message {{messageId}}',
            messageId: pathParams.messageId,
          }),
          err
        )
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (report == null) {
    return null
  }

  return (
    <>
      <iframe id="iFramePrinter" title={t('queues.holocaustReport', 'Holocaust Report')} css={iFrameCss} />
      <Grid>
        {/* Row */}
        <Cell columns={12}>
          <Button onClick={() => backToMessagesList()}>{t('queues.backToMessages', 'Back To Messages')}</Button>
        </Cell>

        {/* Row */}
        <Cell columns={12}>
          <Buttons message={message} backToMessagesList={backToMessagesList} buttonTemplates={buttonTemplates} />
        </Cell>
      </Grid>
      <Grid id="contentToPrint">
        {/* Row */}
        <Cell columns={12}>
          <H5>{`${t('contributor', 'Contributor')}`}</H5>
          <DataRow
            label={`${t('user.label', 'User')}`}
            value={
              <UserLink
                userId={report?.contributorUserData?.cpUserId || ''}
                userName={report?.contributorUserData?.contactName}
                showId
              />
            }
          />
          <DataRow label={`${t('cisId', 'CIS ID')}`} value={report?.contributorUserData?.id} />
          <DataRow label={`${t('userName', 'Username')}`} value={report?.contributorCisData?.users?.[0]?.username} />
          <DataRow
            label={`${t('helperPin', 'Helper PIN')}`}
            value={report?.contributorCisData?.users?.[0]?.helperPin}
          />
          <DataRow
            label={`${t('givenName', 'Given Name')}`}
            value={report?.contributorCisData?.users?.[0]?.givenName}
          />
          <DataRow
            label={`${t('familyName', 'Family Name')}`}
            value={report?.contributorCisData?.users?.[0]?.familyName}
          />
          <DataRow label={`${t('country', 'Country')}`} value={report?.contributorCisData?.users?.[0]?.country} />
          <DataRow
            label={`${t('preferredLanguage', 'Preferred Language')}`}
            value={report?.contributorCisData?.users?.[0]?.preferredLanguage}
          />
          <DataRow label={`${t('email', 'Email')}`} value={report?.contributorCisData?.users?.[0]?.email} />
          <DataRow label={`${t('email', 'Email')}`} value={report?.contributorCisData?.users?.[0]?.membershipNumber} />
          <DataRow
            label={`${t('treePerson', 'Tree Person')}`}
            value={
              <PersonLink
                personId={report?.contributorPersonData?.id}
                personName={report?.contributorPersonData?.summary?.name}
                showId
              />
            }
          />
          <DataRow
            label={`${t('living', 'Living')}`}
            value={printValueOrBooleanReadable(report?.contributorPersonData?.summary?.living)}
          />
        </Cell>

        {/* Row */}
        <Cell columns={12}>
          <H5>{t('ward', 'Ward')}</H5>
          <DataRow
            label={`${t('queues.holocaust.wardId', 'Ward ID')}`}
            value={report?.contributorUserUnit?.unit?.unitNumber}
          />
          <DataRow
            label={`${t('queues.holocaust.wardLocalName', 'Ward Local Name')}`}
            value={report?.contributorUserUnit?.unit?.nameLocal}
          />
          <DataRow
            label={`${t('queues.holocaust.wardLatinName', 'Ward Latin Name')}`}
            value={report?.contributorUserUnit?.unit?.nameLatin}
          />
          <DataRow
            label={`${t('queues.holocaust.stakeId', 'Stake ID')}`}
            value={report?.contributorUserUnit?.parent?.unitNumber}
          />
          <DataRow
            label={`${t('queues.holocaust.stakeLocalName', 'Stake Local Name')}`}
            value={report?.contributorUserUnit?.parent?.nameLocal}
          />
          <DataRow
            label={`${t('queues.holocaust.stakeLatinName', 'Stake Latin Name')}`}
            value={report?.contributorUserUnit?.parent?.nameLatin}
          />
          <DataRow
            label={`${t('queues.holocaust.templeId', 'Temple ID')}`}
            value={report?.contributorUserUnit?.temple?.unitNumber}
          />
          <DataRow
            label={`${t('queues.holocaust.templeLocalName', 'Temple Local Name')}`}
            value={report?.contributorUserUnit?.temple?.nameLocal}
          />
          <DataRow
            label={`${t('queues.holocaust.templeLatinName', 'Temple Latin Name')}`}
            value={report?.contributorUserUnit?.temple?.nameLatin}
          />
        </Cell>

        {/* Row */}
        <Cell columns={12}>
          <H5>{t('queues.holocaust.holocaustVictim', 'Holocaust Victim')}</H5>
          <DataRow
            label={`${t('person.label', 'Person')}`}
            value={
              <PersonLink
                personId={report?.victimPersonData?.id}
                personName={report?.victimPersonData?.summary?.name}
                showId
              />
            }
          />

          <TreeIdRows treeId={message?.details?.treeId} isResearcherTree={message?.details?.researcherTree} />
          <DataRow label={`${t('lifespan', 'Lifespan')}`} value={report?.victimPersonData?.summary?.lifespan} />
          <DataRow
            label={`${t('birthDate', 'Birth Date')}`}
            value={report?.victimPersonData?.birth?.value?.date?.original}
          />
          <DataRow
            label={`${t('birthPlace', 'Birth Place')}`}
            value={report?.victimPersonData?.birth?.value?.place?.original}
          />
          <DataRow
            label={`${t('deathDate', 'Death Date')}`}
            value={report?.victimPersonData?.death?.value?.date?.original}
          />
          <DataRow
            label={`${t('deathPlace', 'Death Place')}`}
            value={report?.victimPersonData?.death?.value?.place?.original}
          />
          <DataRow
            label={`${t('burialDate', 'Burial Date')}`}
            value={report?.victimPersonData?.burial?.value?.date?.original}
          />
          <DataRow
            label={`${t('burialPlace', 'Burial Place')}`}
            value={report?.victimPersonData?.burial?.value?.place?.original}
          />
          <DataRow
            label={`${t('relationshipToContributor', 'Relationship to Contributor')}`}
            value={report?.commonAncestor?.relationSummary}
          />
        </Cell>

        {/* Row */}
        <Cell columns={12}>
          <H5>{t('labels.labels', 'Labels')}</H5>
          {report.victimPersonLabels?.list.map((adminLabel) => {
            return (
              <DataBlock
                key={adminLabel.humanReadableText}
                heading={adminLabel.humanReadableText}
                data={adminLabel.reason}
              />
            )
          })}
        </Cell>

        {/* Row */}
        <Cell columns={12}>
          <H5>{t('performedOrdinances', 'Performed Ordinances')}</H5>
          {report.victimPersonOrdinances?.ordinances
            ?.filter((performedOrdinance) => performedOrdinance.ctrDetail?.ctrId)
            .map((performedOrdinance) => {
              return (
                <div key={performedOrdinance.ctrDetail?.ctrId}>
                  <DataRow label={`${t('type', 'Type')}`} value={performedOrdinance.type} />
                  <DataRow label={`${t('ctrId', 'Ctr ID')}`} value={performedOrdinance.ctrDetail?.ctrId} />
                  <DataRow
                    label={`${t('ctr.performedDate', 'Performed Date')}`}
                    value={performedOrdinance.performedDate}
                  />
                  <DataRow label={`${t('temple.label', 'Temple')}`} value={performedOrdinance.temple} />
                  <p>{'\n\n'}</p>
                </div>
              )
            })}
        </Cell>

        {/* Row */}
        <Cell columns={12}>
          <H5>{t('reservedOrdinances', 'Reserved Ordinances')}</H5>
          {report.victimPersonReservations?.ordinanceReservation?.map((reservedOrdinance) => {
            return (
              <div key={reservedOrdinance.ranId}>
                <DataRow label={`${t('type', 'Type')}`} value={reservedOrdinance.ordinanceType} />
                <DataRow label={`${t('ranId', 'Ran ID')}`} value={reservedOrdinance.ranId} />
                {reservedOrdinance.reserveDate && (
                  <DataRow
                    label={`${t('reservedDate', 'Reserved Date')}`}
                    value={convertUTCString(reservedOrdinance.reserveDate, false, false, ORIGINAL_DATE_FORMAT)}
                  />
                )}
                {reservedOrdinance.assignmentDate && (
                  <DataRow
                    label={`${t('assignmentDate', 'Assignment Date')}`}
                    value={convertUTCString(reservedOrdinance.assignmentDate, false, false, ORIGINAL_DATE_FORMAT)}
                  />
                )}
                {reservedOrdinance.completionDate && (
                  <DataRow
                    label={`${t('completionDate', 'Completion Date')}`}
                    value={convertUTCString(reservedOrdinance.completionDate, false, false, ORIGINAL_DATE_FORMAT)}
                  />
                )}
                <p>{'\n\n'}</p>
              </div>
            )
          })}
        </Cell>
      </Grid>
    </>
  )
}

function printReport() {
  const contentToPrint = document.getElementById('contentToPrint').innerHTML
  const iFramePrinter = document.getElementById('iFramePrinter').contentWindow
  iFramePrinter.document.open()
  iFramePrinter.document.write(contentToPrint)
  iFramePrinter.document.close()
  iFramePrinter.focus()
  iFramePrinter.print()
}

function DataRow({ label, value }) {
  if (value == null) {
    return null
  }
  return <DataBlock inline heading={label} data={value} />
}

function Buttons({ message, backToMessagesList, buttonTemplates }) {
  return (
    <InputGroup>
      <Row>
        <Button emphasis="medium" onClick={() => printReport()}>
          Print
        </Button>
        {buttonTemplates.map((buttonTemplate) =>
          buttonTemplate.isDestructive ? (
            <DangerButton
              key={buttonTemplate.buttonText}
              onClick={() => buttonTemplate.buttonCallback({ message }).then(() => backToMessagesList())}
            >
              {buttonTemplate.buttonText}
            </DangerButton>
          ) : (
            <Button
              key={buttonTemplate.buttonText}
              emphasis="medium"
              onClick={() => buttonTemplate.buttonCallback({ message }).then(() => backToMessagesList())}
            >
              {buttonTemplate.buttonText}
            </Button>
          )
        )}
      </Row>
    </InputGroup>
  )
}
